import React, { useState, useEffect } from 'react';
import { X, Copy, Check, Shield, Zap, RefreshCw, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import {
    WebhookData,
    useGetWebhookByIdQuery,
    useGenerateWebhookUrlMutation,
    useSaveWebhookMutation,
    useUpdateWebhookMutation,
} from '../../../features/integrations/webhookApi';

interface WebhookFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: WebhookData;
}

export const WebhookFormModal: React.FC<WebhookFormModalProps> = ({
    isOpen,
    onClose,
    initialData,
}) => {
    const isEditMode = !!initialData;
    const targetWebhookId = initialData?._id || (initialData as any)?.id || initialData?.webhook_id || '';

    // Fetch full webhook details when editing to get exact auth credentials from CMS
    const { data: detailRes } = useGetWebhookByIdQuery(targetWebhookId, {
        skip: !isOpen || !isEditMode || !targetWebhookId,
    });

    // Form states
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [authType, setAuthType] = useState<'none' | 'basic_auth' | 'bearer_token' | 'api_key'>('none');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [bearerToken, setBearerToken] = useState('');
    const [showBearerToken, setShowBearerToken] = useState(false);
    const [apiKey, setApiKey] = useState('');
    const [showApiKey, setShowApiKey] = useState(false);

    // Generated URL state
    const [generatedUrl, setGeneratedUrl] = useState('');
    const [isCopied, setIsCopied] = useState(false);

    // Mutations
    const [generateUrl, { isLoading: isGenerating }] = useGenerateWebhookUrlMutation();
    const [saveWebhook, { isLoading: isSaving }] = useSaveWebhookMutation();
    const [updateWebhook, { isLoading: isUpdating }] = useUpdateWebhookMutation();

    const resetForm = () => {
        setName('');
        setDescription('');
        setAuthType('none');
        setUsername('');
        setPassword('');
        setShowPassword(false);
        setBearerToken('');
        setShowBearerToken(false);
        setApiKey('');
        setShowApiKey(false);
        setGeneratedUrl('');
        setIsCopied(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    useEffect(() => {
        if (!isOpen) {
            resetForm();
            return;
        }

        if (isEditMode && initialData) {
            const rawItem: any =
                (detailRes as any)?.data?.data ||
                (detailRes as any)?.data ||
                (initialData as any)?.data ||
                initialData;

            if (rawItem && typeof rawItem === 'object') {
                setName(rawItem.name || '');
                setDescription(rawItem.description || '');
                setAuthType(rawItem.auth_type || 'none');
                setGeneratedUrl(rawItem.webhook_url || rawItem.generatedUrl || '');

                const u = rawItem.username ?? rawItem.auth_credentials?.username ?? rawItem.auth?.username ?? rawItem.credentials?.username ?? '';
                const p = rawItem.password ?? rawItem.auth_credentials?.password ?? rawItem.auth?.password ?? rawItem.credentials?.password ?? '';
                const b = rawItem.bearer_token ?? rawItem.bearerToken ?? rawItem.auth_credentials?.bearer_token ?? rawItem.auth?.bearer_token ?? rawItem.token ?? '';
                const k = rawItem.api_key ?? rawItem.apiKey ?? rawItem.auth_credentials?.api_key ?? rawItem.auth?.api_key ?? '';

                setUsername(u);
                setPassword(p);
                setBearerToken(b);
                setApiKey(k);
            }
        } else {
            // New Webhook mode: always ensure pristine empty form
            resetForm();
        }
    }, [isOpen, isEditMode, initialData, detailRes]);

    if (!isOpen) return null;

    const handleCopyUrl = () => {
        if (!generatedUrl) return;
        navigator.clipboard.writeText(generatedUrl);
        setIsCopied(true);
        toast.success('Webhook URL copied to clipboard');
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleGenerateUrl = async () => {
        if (authType === 'basic_auth' && (!username.trim() || !password.trim())) {
            toast.error('Username and password are required for Basic Auth');
            return;
        }
        if (authType === 'bearer_token' && !bearerToken.trim()) {
            toast.error('Bearer Token is required');
            return;
        }
        if (authType === 'api_key' && !apiKey.trim()) {
            toast.error('API Key is required');
            return;
        }

        try {
            const payload: any = { auth_type: authType };
            if (authType === 'basic_auth') {
                payload.username = username.trim();
                payload.password = password.trim();
            } else if (authType === 'bearer_token') {
                payload.bearer_token = bearerToken.trim();
            } else if (authType === 'api_key') {
                payload.api_key = apiKey.trim();
            }

            const res = await generateUrl(payload).unwrap();
            const url = res.webhook_url || res.data?.webhook_url || '';
            if (url) {
                setGeneratedUrl(url);
                toast.success('Generated new Webhook URL');
            } else {
                toast.error('Did not receive a webhook URL from server');
            }
        } catch (err: any) {
            console.error('Failed to generate webhook URL:', err);
            toast.error(err?.data?.message || err?.message || 'Failed to generate webhook URL');
        }
    };

    const handleSubmit = async (e?: React.FormEvent | React.KeyboardEvent) => {
        if (e && 'preventDefault' in e) e.preventDefault();

        if (!name.trim()) {
            toast.error('Please provide a webhook name');
            return;
        }

        if (isEditMode) {
            try {
                const targetId = initialData?._id || (initialData as any)?.id || initialData?.webhook_id;
                const updateBody: any = {
                    id: targetId,
                    name: name.trim(),
                    description: description.trim(),
                    status: initialData?.status || 'active',
                    auth_type: authType,
                };
                if (authType === 'basic_auth') {
                    updateBody.username = username.trim();
                    updateBody.password = password.trim();
                } else if (authType === 'bearer_token') {
                    updateBody.bearer_token = bearerToken.trim();
                } else if (authType === 'api_key') {
                    updateBody.api_key = apiKey.trim();
                }

                await updateWebhook(updateBody).unwrap();
                toast.success('Webhook updated successfully');
                onClose();
            } catch (err: any) {
                console.error('Failed to update webhook:', err);
                toast.error(err?.data?.message || 'Failed to update webhook');
            }
            return;
        }

        // Create Mode
        if (!generatedUrl) {
            toast.error('Please generate a Webhook URL before saving');
            return;
        }

        try {
            const body: any = {
                name: name.trim(),
                description: description.trim(),
                webhook_url: generatedUrl,
                auth_type: authType,
            };
            if (authType === 'basic_auth') {
                body.username = username.trim();
                body.password = password.trim();
            } else if (authType === 'bearer_token') {
                body.bearer_token = bearerToken.trim();
            } else if (authType === 'api_key') {
                body.api_key = apiKey.trim();
            }

            await saveWebhook(body).unwrap();
            toast.success('Webhook created and enabled in CMS');
            onClose();
        } catch (err: any) {
            console.error('Failed to save webhook:', err);
            toast.error(err?.data?.message || 'Failed to save webhook');
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl border border-gray-100 overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#E9F7EF]/50 via-white to-white shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#30A84B]/10 border border-[#30A84B]/20 flex items-center justify-center text-[#30A84B]">
                            <Zap className="w-5 h-5 fill-[#30A84B]/20" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">
                                {isEditMode ? 'Edit Webhook Integration' : 'New Real-Time Webhook'}
                            </h2>
                            <p className="text-xs text-gray-500">
                                {isEditMode
                                    ? 'Update webhook settings, name, and authentication credentials'
                                    : 'Receive external push events and stream real-time JSON to display screens'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form Content (using div to prevent browser login autofill hijacking) */}
                <div
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                            e.preventDefault();
                            handleSubmit(e);
                        }
                    }}
                    className="flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar font-sans text-xs"
                >
                    {/* Decoy inputs to prevent Chrome password manager from autofilling personal user credentials */}
                    <div className="sr-only" aria-hidden="true" style={{ display: 'none' }}>
                        <input type="text" name="fake_user_trap" tabIndex={-1} autoComplete="off" />
                        <input type="password" name="fake_pass_trap" tabIndex={-1} autoComplete="new-password" />
                    </div>

                    {/* Step 1: Authentication & URL */}
                    <div className="space-y-4 p-4 bg-slate-50/70 border border-slate-200/70 rounded-xl">
                        <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-[#30A84B] text-white flex items-center justify-center text-[10px] font-bold">1</span>
                            <h3 className="font-bold text-gray-800 text-sm">Authentication & Endpoint URL</h3>
                        </div>

                        {/* Auth Type Selector */}
                        <div className="space-y-1.5">
                            <label className="block text-[11px] font-bold text-gray-700">Authentication Method</label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {[
                                    { id: 'none', label: 'None', desc: 'Public endpoint' },
                                    { id: 'basic_auth', label: 'Basic Auth', desc: 'User & password' },
                                    { id: 'bearer_token', label: 'Bearer Token', desc: 'Header token' },
                                    { id: 'api_key', label: 'API Key', desc: 'Key param/header' },
                                ].map((t) => (
                                    <button
                                        type="button"
                                        key={t.id}
                                        onClick={() => {
                                            setAuthType(t.id as any);
                                            if (!isEditMode) setGeneratedUrl('');
                                        }}
                                        className={`p-2.5 rounded-lg border text-left transition-all cursor-pointer ${
                                            authType === t.id
                                                ? 'bg-white border-[#30A84B] ring-2 ring-[#30A84B]/10 shadow-sm'
                                                : 'bg-white/70 border-gray-200 hover:border-gray-300'
                                        }`}
                                    >
                                        <div className="font-bold text-[11px] text-gray-800">{t.label}</div>
                                        <div className="text-[9px] text-gray-400 mt-0.5">{t.desc}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Credential Inputs based on Auth Type */}
                        {authType === 'basic_auth' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 animate-in fade-in duration-200">
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-600 mb-1">Username</label>
                                    <input
                                        type="text"
                                        name="wh_custom_param_username"
                                        autoComplete="off"
                                        data-lpignore="true"
                                        data-form-type="other"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        placeholder=""
                                        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white outline-none focus:border-[#30A84B] focus:ring-1 focus:ring-[#30A84B]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-600 mb-1">Password</label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="wh_custom_param_password"
                                            autoComplete="new-password"
                                            data-lpignore="true"
                                            data-form-type="other"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder=""
                                            className="w-full pl-3 pr-8 py-1.5 border border-gray-200 rounded-lg text-xs bg-white outline-none focus:border-[#30A84B] focus:ring-1 focus:ring-[#30A84B]"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 cursor-pointer"
                                            title={showPassword ? 'Hide password' : 'Show password'}
                                        >
                                            {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {authType === 'bearer_token' && (
                            <div className="pt-1 animate-in fade-in duration-200">
                                <label className="block text-[10px] font-bold text-gray-600 mb-1">Bearer Token Secret</label>
                                <div className="relative">
                                    <input
                                        type={showBearerToken ? 'text' : 'password'}
                                        name="wh_custom_param_bearer"
                                        autoComplete="new-password"
                                        data-lpignore="true"
                                        data-form-type="other"
                                        value={bearerToken}
                                        onChange={(e) => setBearerToken(e.target.value)}
                                        placeholder="e.g. secret_token_abc"
                                        className="w-full pl-3 pr-8 py-1.5 border border-gray-200 rounded-lg text-xs bg-white outline-none focus:border-[#30A84B] focus:ring-1 focus:ring-[#30A84B]"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowBearerToken(!showBearerToken)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 cursor-pointer"
                                        title={showBearerToken ? 'Hide token' : 'Show token'}
                                    >
                                        {showBearerToken ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                    </button>
                                </div>
                            </div>
                        )}

                        {authType === 'api_key' && (
                            <div className="pt-1 animate-in fade-in duration-200">
                                <label className="block text-[10px] font-bold text-gray-600 mb-1">API Key</label>
                                <div className="relative">
                                    <input
                                        type={showApiKey ? 'text' : 'password'}
                                        name="wh_custom_param_apikey"
                                        autoComplete="new-password"
                                        data-lpignore="true"
                                        data-form-type="other"
                                        value={apiKey}
                                        onChange={(e) => setApiKey(e.target.value)}
                                        placeholder="e.g. api_key_xyz"
                                        className="w-full pl-3 pr-8 py-1.5 border border-gray-200 rounded-lg text-xs bg-white outline-none focus:border-[#30A84B] focus:ring-1 focus:ring-[#30A84B]"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowApiKey(!showApiKey)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 cursor-pointer"
                                        title={showApiKey ? 'Hide API key' : 'Show API key'}
                                    >
                                        {showApiKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                    </button>
                                </div>
                            </div>
                        )}

                        {authType === 'none' && (
                            <p className="text-[11px] text-gray-500 bg-white/70 p-2.5 rounded-lg border border-gray-200/70">
                                No authentication credentials required for this endpoint.
                            </p>
                        )}

                        {/* Generate URL Button & Result Field */}
                        <div className="pt-1">
                            {!isEditMode && !generatedUrl ? (
                                <button
                                    type="button"
                                    onClick={handleGenerateUrl}
                                    disabled={isGenerating}
                                    className="w-full py-2.5 bg-[#30A84B] hover:bg-[#28913F] disabled:opacity-50 text-white rounded-lg font-bold text-xs flex items-center justify-center gap-2 shadow-sm transition-all cursor-pointer"
                                >
                                    {isGenerating ? (
                                        <>
                                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                            Generating Endpoint...
                                        </>
                                    ) : (
                                        <>
                                            <Zap className="w-3.5 h-3.5" />
                                            Generate Webhook URL
                                        </>
                                    )}
                                </button>
                            ) : (
                                <div className="space-y-1.5">
                                    <div className="flex items-center justify-between text-[10px]">
                                        <span className="font-bold text-gray-700 flex items-center gap-1 text-emerald-600">
                                            <Check className="w-3.5 h-3.5" /> Webhook Receiver URL
                                        </span>
                                        {!isEditMode && (
                                            <button
                                                type="button"
                                                onClick={handleGenerateUrl}
                                                disabled={isGenerating}
                                                className="text-[#30A84B] hover:underline font-semibold"
                                            >
                                                Regenerate
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg p-1.5">
                                        <input
                                            type="text"
                                            readOnly
                                            value={generatedUrl}
                                            className="w-full bg-transparent font-mono text-[11px] text-gray-700 outline-none px-1 select-all"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleCopyUrl}
                                            className="px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-[10px] font-bold flex items-center gap-1 transition-colors cursor-pointer shrink-0"
                                        >
                                            {isCopied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                                            {isCopied ? 'Copied' : 'Copy'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Step 2: Identification */}
                    <div className="space-y-4 p-4 bg-slate-50/70 border border-slate-200/70 rounded-xl">
                        <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-[#30A84B] text-white flex items-center justify-center text-[10px] font-bold">2</span>
                            <h3 className="font-bold text-gray-800 text-sm">Webhook Details</h3>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="block text-[11px] font-bold text-gray-700 mb-1">
                                    Webhook Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. Hospital OPD Queue, Sales Counter Alert"
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs bg-white outline-none focus:border-[#30A84B] focus:ring-1 focus:ring-[#30A84B]"
                                />
                            </div>

                            <div>
                                <label className="block text-[11px] font-bold text-gray-700 mb-1">Description (Optional)</label>
                                <textarea
                                    rows={2}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Describe the data feed or source sending events to this screen..."
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs bg-white outline-none focus:border-[#30A84B] focus:ring-1 focus:ring-[#30A84B] resize-none"
                                />
                            </div>
                        </div>
                    </div>

                    {/* How it works Callout */}
                    <div className="p-3.5 bg-[#E9F7EF] border border-[#30A84B]/20 rounded-xl space-y-1.5 text-[#101928]">
                        <div className="font-bold flex items-center gap-1.5 text-[11px]">
                            <Shield className="w-3.5 h-3.5 text-[#30A84B]" />
                            How it works:
                        </div>
                        <p className="text-[10px] text-gray-700 leading-relaxed">
                            Once saved, send any JSON payload via <code className="bg-[#30A84B]/10 text-[#30A84B] px-1 py-0.5 rounded font-mono font-bold">POST</code> to the webhook receiver URL. The data will immediately appear in the Template Editor canvas bindings tree under Webhook Source.
                        </p>
                    </div>

                    {/* Footer Actions */}
                    <div className="pt-2 flex items-center justify-end gap-3 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg font-semibold hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => handleSubmit()}
                            disabled={isSaving || isUpdating || (!isEditMode && !generatedUrl)}
                            className="px-5 py-2 bg-[#30A84B] hover:bg-[#28913F] disabled:opacity-50 text-white rounded-lg font-bold shadow-md flex items-center gap-1.5 transition-all cursor-pointer"
                        >
                            {isSaving || isUpdating ? (
                                <>
                                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Check className="w-4 h-4" />
                                    {isEditMode ? 'Update Webhook' : 'Save & Enable Webhook'}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
